import clsx from 'clsx';
import React, { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState, } from 'react';
import { Checkbox } from '../Checkbox';
import { TableCell } from './TableCell';
import { useTableContextSelector } from './TableContext';
import { TableHeader } from './TableHeader';
import { useTableSectionContext } from './TableSectionContext';
import { canUseDOM, useComposedRefs } from '../../utils';
import { Tooltip } from '../Tooltip';
export const TableRow = forwardRef(function TableRow(props, ref) {
    const { children, className, ariaCheckboxLabel, index: indexProp, value, disabled, checkboxTooltip, onClick, id: userId, hidden, ...rest } = props;
    const metaKeyPressed = useMetaKeyPressed();
    const [element, setElement] = useState(null);
    const [hasFocus, setHasFocus] = useState(false);
    const rowRef = useRef(null);
    const inputRef = useRef(null);
    const uniqueId = useId();
    const id = userId !== null && userId !== void 0 ? userId : uniqueId;
    const onSelect = useTableContextSelector(({ onSelect }) => onSelect);
    const onSelectAll = useTableContextSelector(({ onSelectAll }) => onSelectAll);
    const selectableValues = useTableContextSelector(({ selectableValues }) => selectableValues);
    const selected = useTableContextSelector(({ selected }) => selected.includes(value));
    const allSelected = useTableContextSelector(({ allSelected }) => allSelected);
    const tableSelectable = useTableContextSelector(({ selectable }) => selectable);
    const hadKeyboardEvent = useTableContextSelector(({ hadKeyboardEvent }) => hadKeyboardEvent);
    const { section } = useTableSectionContext() || {};
    useEffect(() => {
        if (value != null && !disabled) {
            selectableValues.add(value);
        }
        return () => {
            selectableValues.delete(value);
        };
    }, [disabled, selectableValues, value]);
    const SelectableElement = section === 'head' ? TableHeader : TableCell;
    const selectable = !disabled && tableSelectable && section !== 'head';
    const clickable = selectable || isClickable(element, onClick);
    const checked = section === 'head' ? allSelected : selected;
    // we need to track the `before-hover` state or we lose it upon re-rendering this component
    const preserveClassList = useRef([]);
    const observer = useRef(null);
    useEffect(() => {
        if (!element) {
            return;
        }
        observer.current = new MutationObserver((mutations) => {
            mutations.forEach((mu) => {
                if (mu.type !== 'attributes' && mu.attributeName !== 'class')
                    return;
                if (mu.target.classList.contains('mollie-ui-table-row--before-hover')) {
                    preserveClassList.current = ['mollie-ui-table-row--before-hover'];
                }
                else {
                    preserveClassList.current = [];
                }
            });
        });
        observer.current.observe(element, { attributes: true });
        return () => {
            if (!observer.current)
                return;
            observer.current.disconnect();
            observer.current = null;
        };
    }, [element]);
    const checkbox = (React.createElement(Checkbox, { value: value, checked: checked, disabled: disabled, onChange: () => {
            if (section === 'head') {
                onSelectAll();
            }
            else {
                onSelect(value);
            }
        }, onClick: (e) => {
            e.stopPropagation();
        }, className: "mollie-ui-table-checkbox", UNSAFE_indicatorClassName: "mollie-ui-table-checkbox-indicator", UNSAFE_inputTabIndex: isClickable(element, onClick) ? -1 : undefined, UNSAFE_contentClassName: "mollie-ui-visually-hidden", inputRef: inputRef, "data-table-row-tabbable": section !== 'head' ? true : undefined }, ariaCheckboxLabel));
    const previousElement = useRef(null);
    useLayoutEffect(() => {
        if (rowRef.current) {
            setBeforeClassNames(rowRef.current);
            setBeforeClassNames(findPreviousElement(rowRef.current));
        }
    });
    return (React.createElement("tr", { ...rest, className: clsx('mollie-ui-table-row', {
            'mollie-ui-table-row--hidden': hidden,
            'mollie-ui-table-row--clickable': clickable,
            'mollie-ui-table-row--disabled': disabled,
            'mollie-ui-table-row--has-focus': hasFocus && hadKeyboardEvent,
            'mollie-ui-table-row--selected': tableSelectable && selected,
        }, preserveClassList.current, className), "aria-selected": tableSelectable ? selected : undefined, "aria-disabled": tableSelectable ? disabled : undefined, role: "row", ref: useComposedRefs(rowRef, setElement, ref), onClick: (e) => {
            var _a, _b, _c;
            let actuallyClickable = clickable;
            const tableRowAction = e.currentTarget.querySelector('[data-table-row-action]');
            if (tableRowAction) {
                actuallyClickable = true;
            }
            if (!actuallyClickable)
                return;
            if (!((_a = rowRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                return;
            }
            if (tableRowAction instanceof HTMLElement) {
                if (metaKeyPressed.current) {
                    tableRowAction.dispatchEvent(new MouseEvent('click', { metaKey: true }));
                }
                else {
                    tableRowAction.click();
                }
            }
            else if ((!onClick && inputRef.current) ||
                ((_c = (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.parentNode) === null || _c === void 0 ? void 0 : _c.contains(e.target))) {
                e.preventDefault();
                inputRef.current.click();
            }
            else {
                onClick === null || onClick === void 0 ? void 0 : onClick(e);
            }
        }, onMouseEnter: (e) => {
            var _a;
            let actuallyClickable = clickable;
            try {
                if (e.currentTarget.querySelector(':has([data-table-row-action])')) {
                    actuallyClickable = true;
                }
            }
            catch (e) { }
            if (!actuallyClickable)
                return;
            previousElement.current = findPreviousElement(e.currentTarget);
            if ((_a = previousElement.current) === null || _a === void 0 ? void 0 : _a.classList.contains('mollie-ui-table-row')) {
                previousElement.current.classList.add('mollie-ui-table-row--before-hover');
            }
        }, onMouseLeave: () => {
            var _a;
            (_a = previousElement.current) === null || _a === void 0 ? void 0 : _a.classList.remove('mollie-ui-table-row--before-hover');
        }, onKeyDown: (e) => {
            if (e.key === ' ' && inputRef.current) {
                e.preventDefault();
                inputRef.current.click();
            }
            else if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.click();
            }
        }, onFocus: () => {
            setHasFocus(true);
        }, onBlur: () => {
            setHasFocus(false);
        }, id: id },
        tableSelectable && (React.createElement(SelectableElement, { className: "mollie-ui-table-cell-checkbox", gridArea: "checkbox", alignSelf: "center" },
            React.createElement("div", { className: "mollie-ui-table-checkbox-wrapper" }, checkboxTooltip ? (React.createElement(Tooltip, { tabIndex: -1, trigger: React.createElement("div", { className: "mollie-ui-table-tooltip-wrapper" }, checkbox) }, checkboxTooltip)) : (checkbox)))),
        children));
});
TableRow.displayName = 'Table.Row';
function findPreviousElement(element) {
    var _a, _b;
    let previousElement = element.previousElementSibling;
    if (!previousElement &&
        ((_b = (_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.previousElementSibling) === null || _b === void 0 ? void 0 : _b.classList.contains('mollie-ui-table-body'))) {
        previousElement =
            element.parentElement.previousElementSibling.lastElementChild;
        while (previousElement &&
            previousElement.classList.contains('mollie-ui-table-row--hidden')) {
            previousElement = previousElement.previousElementSibling;
        }
    }
    return previousElement;
}
function findNextElement(element) {
    var _a, _b;
    let nextElement = element.nextElementSibling;
    if (!nextElement &&
        ((_b = (_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.nextElementSibling) === null || _b === void 0 ? void 0 : _b.classList.contains('mollie-ui-table-body'))) {
        nextElement = element.parentElement.nextElementSibling.lastElementChild;
        while (nextElement &&
            nextElement.classList.contains('mollie-ui-table-row--hidden')) {
            nextElement = nextElement.nextElementSibling;
        }
    }
    return nextElement;
}
function findNextGroupElement(element) {
    var _a, _b, _c;
    const nextGroupElement = (_b = (_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.nextElementSibling) === null || _b === void 0 ? void 0 : _b.firstElementChild;
    if ((_c = nextGroupElement === null || nextGroupElement === void 0 ? void 0 : nextGroupElement.parentElement) === null || _c === void 0 ? void 0 : _c.classList.contains('mollie-ui-table-group')) {
        return nextGroupElement;
    }
    return;
}
function setBeforeClassNames(element) {
    var _a;
    if (!element)
        return;
    const nextElement = findNextElement(element);
    const nextGroupElement = findNextGroupElement(element);
    if ((_a = nextGroupElement === null || nextGroupElement === void 0 ? void 0 : nextGroupElement.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('mollie-ui-table-group--open')) {
        element.classList.add('mollie-ui-table-row--before-open-group');
    }
    else {
        element.classList.remove('mollie-ui-table-row--before-open-group');
    }
    if (nextElement === null || nextElement === void 0 ? void 0 : nextElement.classList.contains('mollie-ui-table-row--selected')) {
        element.classList.add('mollie-ui-table-row--before-selected');
    }
    else {
        element.classList.remove('mollie-ui-table-row--before-selected');
    }
}
function useMetaKeyPressed() {
    const metaKeyPressedRef = useRef(false);
    function downHandler({ metaKey }) {
        metaKeyPressedRef.current = metaKey;
    }
    function upHandler() {
        metaKeyPressedRef.current = false;
    }
    useEffect(() => {
        if (!canUseDOM())
            return;
        window.addEventListener('keydown', downHandler);
        window.addEventListener('keyup', upHandler);
        return () => {
            window.removeEventListener('keydown', downHandler);
            window.removeEventListener('keyup', upHandler);
        };
    }, []);
    return metaKeyPressedRef;
}
// eslint-disable-next-line @typescript-eslint/ban-types
function isClickable(element, onClick) {
    if (onClick != null) {
        return true;
    }
    const tableRowAction = element === null || element === void 0 ? void 0 : element.querySelector('[data-table-row-action]');
    return !!tableRowAction;
}
//# sourceMappingURL=TableRow.js.map